import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { getMaxValue, getMinValue } from '@care-portals/shared';

import * as ConsultationActions from './consultation.actions';
import { ConsultationEntity } from './consultation.models';

export const CONSULTATION_FEATURE_KEY = 'consultation';

export interface State extends EntityState<ConsultationEntity> {
  selectedId?: string | number; // which Consultation record has been selected
  loading: boolean;
  creating: boolean;
  error?: string | null; // last none error (if any)
  minId: string;
  maxId: string;
}

export interface ConsultationPartialState {
  readonly [CONSULTATION_FEATURE_KEY]: State;
}

export function selectConsultationId(consultation: ConsultationEntity): string {
  //In this case this would be optional since primary key is id
  return consultation._id;
}

export const consultationAdapter: EntityAdapter<ConsultationEntity> =
  createEntityAdapter<ConsultationEntity>({
    selectId: selectConsultationId
  });

export const initialState: State = consultationAdapter.getInitialState({
  // set initial required properties
  loading: false,
  updated: false,
  creating: false,
  minId: null,
  maxId: null
});

const consultationReducer = createReducer(
  initialState,
  on(ConsultationActions.resetConsultations, state =>
    consultationAdapter.removeAll({
      ...state,
      error: null,
      minId: null,
      maxId: null
    })
  ),
  on(ConsultationActions.loadConsultations, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    ConsultationActions.loadConsultationsSuccess,
    (state, { consultations, loadType }) => {
      const ids = consultations.map(consultation => consultation._id);
      const newMaxId = getMaxValue(ids);
      const newMinId = getMinValue(ids);
      const maxId = newMaxId
        ? state.maxId > newMaxId
          ? state.maxId
          : newMaxId
        : state.maxId;
      const minId = newMinId
        ? state.minId < newMinId
          ? state.minId
          : newMinId
        : state.minId;

      const currentConsultations = Object.keys(state.entities).map(
        key => state.entities[key]
      );
      const newConsultations =
        loadType === 'newer'
          ? [...consultations, ...currentConsultations]
          : [...currentConsultations, ...consultations];

      return consultationAdapter.setAll(newConsultations, {
        ...state,
        loading: false,
        maxId,
        minId
      });
    }
  ),
  on(ConsultationActions.loadConsultationsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(ConsultationActions.saveConsultation, state => ({
    ...state,
    creating: true,
    error: null
  })),
  on(ConsultationActions.saveConsultationSuccess, (state, { consultation }) =>
    consultationAdapter.addOne(consultation, { ...state, creating: false })
  ),
  on(ConsultationActions.saveConsultationFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  })),

  on(ConsultationActions.updateConsultation, state => ({
    ...state,
    updated: false,
    error: null
  })),
  on(ConsultationActions.updateConsultationSuccess, (state, { consultation }) =>
    consultationAdapter.updateOne(
      { id: consultation._id, changes: consultation },
      { ...state, updated: true }
    )
  ),
  on(ConsultationActions.updateConsultationFailure, (state, { error }) => ({
    ...state,
    error
  })),

  on(ConsultationActions.setCurrentConsultation, (state, { id }) => ({
    ...state,
    selectedId: id
  })),

  on(ConsultationActions.clearNotification, (state, { consultation }) =>
    consultationAdapter.updateOne(
      {
        id: consultation._id,
        changes: {
          customer: Object.assign({}, consultation.customer, {
            notificationsCount: 0
          })
        }
      },
      { ...state }
    )
  )
);

export function reducer(state: State | undefined, action: Action) {
  return consultationReducer(state, action);
}
